import {
  MenuBook as CodexIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Business as OrgIcon,
  AccountCircle as ProfileIcon,
  CheckCircle as SelectedIcon,
  AutoAwesome as SparklesIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImageLightbox } from '@/components/image-lightbox';
import { SecureImage } from '@/components/secure-image';
import { useFirebase } from '@/firebase';
import { useGenerateEntityVisual } from '@/hooks/use-generate-entity-visual';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import type { PrimaryEntityType, ProcessedImage } from '@/lib/types';

export type EntityType = PrimaryEntityType | 'codex';

interface EntityImageManagerProps {
  type: EntityType;
  images: ProcessedImage[];
  primaryImageId: string | null;
  onUpdate: (updates: { images?: ProcessedImage[], pendingFiles?: File[], primaryImageId?: string | null }) => void;
  pendingFiles?: File[];
  isProcessing?: boolean;
  maxImages?: number;
  // New props for AI generation
  entityId: string;
  canCreateAIImages?: boolean;
  isTokenDepleted?: boolean;
  visualDescription?: string;
  onVisualDescriptionChange?: (value: string) => void;
  disableDeletion?: boolean;
}

const EMPTY_FILES: File[] = [];

export function EntityImageManager({
  type,
  images,
  primaryImageId,
  onUpdate,
  pendingFiles = EMPTY_FILES,
  isProcessing = false,
  maxImages = 5,
  entityId,
  canCreateAIImages = false,
  isTokenDepleted = false,
  visualDescription = '',
  onVisualDescriptionChange,
  disableDeletion = false,
}: EntityImageManagerProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { generateEntityVisual, loading: generatingAI } = useGenerateEntityVisual();
  const { selectedNovelId: novelId } = useNovelRepository();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { storage } = useFirebase();

  const [previews, setPreviews] = useState<string[]>([]);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [tempDescription, setTempDescription] = useState(visualDescription);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxPath, setLightboxPath] = useState<string | null>(null);

  const MIN_DESCRIPTION_LENGTH = 50;
  const MAX_DESCRIPTION_LENGTH = 1500;

  const handleZoom = (url: string, path?: string | null) => {
    setLightboxUrl(url);
    setLightboxPath(path || null);
    setIsLightboxOpen(true);
  };

  useEffect(() => {
    if (!pendingFiles || pendingFiles.length === 0) {
      setPreviews(prev => prev.length === 0 ? prev : []);
      return;
    }
    const newPreviews = pendingFiles.map(file => URL.createObjectURL(file));
    setPreviews(newPreviews);
    return () => newPreviews.forEach(url => URL.revokeObjectURL(url));
  }, [pendingFiles]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const totalCurrent = images.length + pendingFiles.length;
    const remainingSlots = maxImages - totalCurrent;

    if (remainingSlots <= 0) {
      toast({
        title: t('entity_image_manager.common.limit_reached'),
        description: t('entity_image_manager.common.limit_reached_desc', { max: maxImages }),
        variant: 'destructive',
      });
      return;
    }

    const newFiles = Array.from(selectedFiles).slice(0, remainingSlots);
    onUpdate({ pendingFiles: [...pendingFiles, ...newFiles] });

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAIGenerate = async () => {
    if (visualDescription.length < MIN_DESCRIPTION_LENGTH) {
      setTempDescription(visualDescription);
      setDescriptionDialogOpen(true);
      return;
    }

    generate();
  };

  const generate = async (desc: string = visualDescription) => {
    const totalCurrent = images.length + pendingFiles.length;
    if (totalCurrent >= maxImages) {
      toast({
        title: t('entity_image_manager.common.limit_reached'),
        description: t('entity_image_manager.common.limit_reached_desc', { max: maxImages }),
        variant: 'destructive',
      });
      return;
    }

    // Collect existing image URLs as references for consistent generation
    const referenceImageUrls = images
      .map(img => img.original?.url || img.profile?.url)
      .filter((url): url is string => !!url);

    const result = await generateEntityVisual({
      novelId: novelId || '',
      entityId,
      entityType: type,
      visualDescription: desc,
      referenceImageUrls,
    });

    if (result === 'PENDING') {
      toast({
        title: t('entity_image_manager.common.ai_generation_pending'),
        description: t('entity_image_manager.common.ai_generation_pending_desc'),
      });
      return;
    }

    if (result) {
      toast({
        title: t('entity_image_manager.common.ai_generation_success'),
        description: t('entity_image_manager.common.ai_generation_success_desc'),
      });
    } else {
      toast({
        title: t('entity_image_manager.common.ai_generation_error'),
        description: t('entity_image_manager.common.ai_generation_error_desc'),
        variant: 'destructive'
      });
    }
  };

  const handleDialogConfirm = () => {
    if (tempDescription.length < MIN_DESCRIPTION_LENGTH) {
      toast({
        title: t('entity_image_manager.common.ai_description_error'),
        variant: 'destructive'
      });
      return;
    }

    onVisualDescriptionChange?.(tempDescription);
    setDescriptionDialogOpen(false);
    generate(tempDescription);
  };

  const setAsPrimary = (imageId: string) => {
    onUpdate({ primaryImageId: imageId });
    toast({
      title: t(`entity_image_manager.${type}.toast_profile_updated`),
      description: t(`entity_image_manager.${type}.toast_profile_updated_desc`),
    });
  };

  const removeImage = async (index: number, isPending: boolean) => {
    if (isPending) {
      const nextFiles = pendingFiles.filter((_, i) => i !== index);
      onUpdate({ pendingFiles: nextFiles });
    } else {
      const nextImages = images.filter((_, i) => i !== index);
      const deletingPrimary = images[index]?.id === primaryImageId;
      const nextPrimaryImageId = deletingPrimary
        ? (nextImages[index]?.id ?? nextImages[nextImages.length - 1]?.id ?? null)
        : primaryImageId;
      onUpdate({ images: nextImages, primaryImageId: nextPrimaryImageId });
    }

    toast({
      title: t('entity_image_manager.common.toast_image_removed'),
      description: t('entity_image_manager.common.toast_image_removed_desc'),
    });
  };

  const allImages = [
    ...images.map(img => {
      const profile = img.profile;
      const original = img.original;
      const bestUrl = profile?.url || original?.url || '';
      const bestPath = profile?.path || original?.path || '';

      return {
        id: img.id,
        url: bestUrl,
        path: bestPath,
        originalUrl: original?.url || bestUrl,
        originalPath: original?.path || bestPath,
        isPending: false
      };
    }),
    ...previews.map(url => ({ id: undefined, url, path: undefined, originalUrl: url, originalPath: undefined, isPending: true }))
  ];

  const activeImage = allImages.find(image => image.id === primaryImageId);
  const activeProfileUrl = activeImage?.url || null;
  const activeProfilePath = activeImage?.path || null;
  const activeOriginalUrl = activeImage?.originalUrl || null;
  const activeOriginalPath = activeImage?.originalPath || null;

  const getIcon = () => {
    switch (type) {
      case 'characters': return <ProfileIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.3 }} />;
      case 'organizations': return <OrgIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.3 }} />;
      case 'locations': return <ImageIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.3 }} />;
      case 'codex': return <CodexIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.3 }} />;
    }
  };

  const getActionIcon = () => {
    switch (type) {
      case 'characters': return <ProfileIcon fontSize="small" />;
      case 'organizations': return <OrgIcon fontSize="small" />;
      case 'locations': return <ImageIcon fontSize="small" />;
      case 'codex': return <CodexIcon fontSize="small" />;
    }
  };

  const getActionTooltip = () => {
    switch (type) {
      case 'characters': return t('entity_image_manager.common.set_profile');
      case 'organizations': return t('entity_image_manager.common.set_visual');
      case 'locations': return t('entity_image_manager.common.set_primary');
      case 'codex': return t('entity_image_manager.common.set_primary');
    }
  };

  const aspectRatio = type === 'locations' ? '16/9' : '1';

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {t(`entity_image_manager.${type}.title`)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {t(`entity_image_manager.${type}.description`, { max: maxImages })}
      </Typography>

      {/* Main Preview */}
      <Box
        onClick={() => activeOriginalUrl && handleZoom(activeOriginalUrl, activeOriginalPath)}
        sx={{
          aspectRatio,
          width: '100%',
          bgcolor: 'action.hover',
          borderRadius: 1.5,
          overflow: 'hidden',
          position: 'relative',
          mb: 2,
          border: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: activeOriginalUrl ? 'zoom-in' : 'default',
          '&:hover img': { transform: activeOriginalUrl ? 'scale(1.02)' : 'none' },
          '& img': { transition: 'transform 0.3s ease' }
        }}
      >
        {activeProfileUrl ? (
          <SecureImage
            src={activeProfileUrl}
            path={activeProfilePath || undefined}
            alt="Primary visual"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: type === 'characters' ? 'top' : 'center',
            }}
          />
        ) : (
          getIcon()
        )}

        {(isProcessing || generatingAI) && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, color: 'white' }}>
            <CircularProgress size={32} color="inherit" sx={{ mb: 1 }} />
            <Typography variant="caption">
              {generatingAI ? t('common.loading') : t(`entity_image_manager.${type}.processing`)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Gallery */}
      {allImages.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
            {t('entity_image_manager.common.gallery', { current: allImages.length, max: maxImages })}
          </Typography>
          <Grid container spacing={1}>
            {allImages.map((img, idx) => {
              const isSelected = !!img.id && primaryImageId === img.id;
              return (
                <Grid size={2.4} key={idx}>
                  <Box
                    sx={{
                      aspectRatio: '1',
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                      border: 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      cursor: 'pointer',
                      bgcolor: 'action.hover',
                      '&:hover .actions': { opacity: 1 }
                    }}
                  >
                    <SecureImage
                      src={img.url}
                      path={img.path || undefined}
                      alt={`Gallery image ${idx}`}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: type === 'characters' ? 'top' : 'center',
                        opacity: img.isPending ? 0.7 : 1,
                      }}
                    />

                    {/* Pending Indicator */}
                    {img.isPending && (
                      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, p: 0.2, bgcolor: 'rgba(0,0,0,0.4)', color: 'white', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ fontSize: '8px' }}>{t('entity_image_manager.common.pending')}</Typography>
                      </Box>
                    )}

                    {/* Tiny Selection Indicator */}
                    {isSelected && (
                      <SelectedIcon
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          fontSize: 14,
                          color: 'primary.main',
                          bgcolor: 'background.paper',
                          borderRadius: '50%'
                        }}
                      />
                    )}

                    {/* Actions Overlay */}
                    <Stack
                      className="actions"
                      direction="row"
                      spacing={0.5}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(0,0,0,0.4)',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      {!img.isPending && img.id && !isSelected && (
                        <Tooltip title={getActionTooltip()}>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAsPrimary(img.id as string); }} sx={{ p: 0.5, color: 'white' }}>
                            {getActionIcon()}
                          </IconButton>
                        </Tooltip>
                      )}
                      {!disableDeletion && (
                        <Tooltip title={t('entity_image_manager.common.delete')}>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeImage(img.isPending ? (idx - images.length) : idx, img.isPending); }} sx={{ p: 0.5, color: 'error.main' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <input
        type="file"
        hidden
        multiple
        ref={fileInputRef}
        accept="image/*"
        onChange={handleFileUpload}
      />

      <Stack spacing={1}>
        <Tooltip
          title={allImages.length >= maxImages ? t('entity_image_manager.common.limit_reached_desc', { max: maxImages }) : ''}
          placement="top"
        >
          <div style={{ width: '100%' }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing || generatingAI || allImages.length >= maxImages}
              sx={{ borderRadius: 1.5, py: 1 }}
            >
              {t(`entity_image_manager.${type}.button`)}
            </Button>
          </div>
        </Tooltip>

        {canCreateAIImages && (
          <Tooltip
            title={
              allImages.length >= maxImages
                ? t('entity_image_manager.common.limit_reached_desc', { max: maxImages })
                : isTokenDepleted
                  ? t('entity_image_manager.common.tokens_depleted_tooltip')
                  : ''
            }
            placement="top"
          >
            <div style={{ width: '100%' }}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                startIcon={generatingAI ? <CircularProgress size={18} color="inherit" /> : <SparklesIcon />}
                onClick={handleAIGenerate}
                disabled={isProcessing || generatingAI || allImages.length >= maxImages || isTokenDepleted}
                sx={{
                  borderRadius: 1.5,
                  py: 1,
                  borderWidth: 2,
                  '&:hover': { borderWidth: 2 }
                }}
              >
                {t('entity_image_manager.common.ai_generate')}
              </Button>
            </div>
          </Tooltip>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic', textAlign: 'center' }}>
        {t('entity_image_manager.common.footer_note')}
      </Typography>

      {/* AI Description Dialog */}
      <Dialog
        open={descriptionDialogOpen}
        onClose={() => setDescriptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SparklesIcon color="secondary" />
          {t('entity_image_manager.common.ai_description_title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('entity_image_manager.common.ai_description_desc', { min: MIN_DESCRIPTION_LENGTH })}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            autoFocus
            value={tempDescription}
            onChange={(e) => setTempDescription(e.target.value)}
            placeholder={t('entity_image_manager.common.ai_description_placeholder')}
            inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
            error={tempDescription.length > 0 && tempDescription.length < MIN_DESCRIPTION_LENGTH}
            helperText={
              tempDescription.length > 0 && tempDescription.length < MIN_DESCRIPTION_LENGTH
                ? t('entity_image_manager.common.ai_description_error')
                : `${tempDescription.length} / ${MAX_DESCRIPTION_LENGTH}`
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDescriptionDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleDialogConfirm}
            disabled={tempDescription.length < MIN_DESCRIPTION_LENGTH}
            startIcon={<SparklesIcon />}
          >
            {t('entity_image_manager.common.ai_generate')}
          </Button>
        </DialogActions>
      </Dialog>

      <ImageLightbox
        open={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        imageUrl={lightboxUrl}
        imagePath={lightboxPath}
        altText={t('entity_image_manager.common.view_image')}
      />
    </Paper>
  );
}
